import '../styles/TodoList.css';
import React, { useEffect, useState, useRef } from "react";
import { auth, db } from "../config/firebase";
import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore';
import axios from 'axios';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { DataView } from 'primereact/dataview';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { InputSwitch } from 'primereact/inputswitch';
import { Checkbox } from 'primereact/checkbox';
import { MultiSelect } from 'primereact/multiselect';
import { Chip } from 'primereact/chip';
import { Sidebar } from 'primereact/sidebar';
import { Divider } from 'primereact/divider';
import { Card } from 'primereact/card';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { SelectButton } from 'primereact/selectbutton';
import { avatars } from '../components/logInUserInformation/ProfileAvatar';
import { Hearts } from 'react-loader-spinner';

export const TodoList = () => {
    const toast = useRef(null);
    const [todoList, setTodoList] = useState([]);
    const [partnerTodos, setPartnerTodos] = useState([]);
    const [ownerFilter, setOwnerFilter] = useState(null); // null = all, true = current user, false = partner

    const [connectedUserId, setConnectedUserId] = useState(null);
    const [userName, setUserName] = useState('');
    const [partnerName, setPartnerName] = useState('');

    const [todo, setTodo] = useState("");
    const [dueDate, setDueDate] = useState(null);
    const [priority, setPriority] = useState('medium');
    const [category, setCategory] = useState('');
    const [assigneeId, setAssigneeId] = useState(null);
    const [assigneeName, setAssigneeName] = useState(null);

    // UI states
    const [loading, setLoading] = useState(true);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [currentTodo, setCurrentTodo] = useState(null);
    const [filterSidebarVisible, setFilterSidebarVisible] = useState(false);

    // Filter and sort options
    const [filterStatus, setFilterStatus] = useState(null);
    const [filterPriority, setFilterPriority] = useState(null);
    const [filterCategories, setFilterCategories] = useState([]);
    const [sortField, setSortField] = useState('dueDate');
    const [sortOrder, setSortOrder] = useState('asc');
    const [searchTerm, setSearchTerm] = useState('');
    const [quickFilter, setQuickFilter] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // Options
    const priorityOptions = [
        { label: 'High', value: 'high' },
        { label: 'Medium', value: 'medium' },
        { label: 'Low', value: 'low' }
    ];


    const categoryOptions = [
        { label: 'Work', value: 'work' },
        { label: 'Personal', value: 'personal' },
        { label: 'Shopping', value: 'shopping' },
        { label: 'Health', value: 'health' },
        { label: 'Education', value: 'education' }
    ];

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Dynamically create assignee options based on current user and partner
    const getAssigneeOptions = () => {
        const options = [];

        if (auth.currentUser) {
            options.push({
                label: userName || 'You',
                value: auth.currentUser.uid,
                name: userName || 'You'
            });
        }

        // Add partner option if there's a connected user, even if name isn't loaded yet
        if (connectedUserId) {
            options.push({
                label: partnerName || 'Partner',
                value: connectedUserId,
                name: partnerName || 'Partner'
            });
        }

        return options;
    };

    const sortOptions = [
        { label: 'Due Date (Earliest)', value: { field: 'dueDate', order: 'asc' } },
        { label: 'Due Date (Latest)', value: { field: 'dueDate', order: 'desc' } },
        { label: 'Priority (Highest)', value: { field: 'priority', order: 'asc' } },
        { label: 'Priority (Lowest)', value: { field: 'priority', order: 'desc' } },
        { label: 'Created (Newest)', value: { field: 'createdAt', order: 'desc' } },
        { label: 'Created (Oldest)', value: { field: 'createdAt', order: 'asc' } }
    ];

    const quickFilterOptions = [
        { label: 'All Tasks', value: 'all' },
        { label: "Today's Tasks", value: 'today' },
        { label: 'This Week', value: 'week' },
        { label: 'Pending', value: 'pending' },
        { label: 'Completed', value: 'completed' }
    ];

    const [userAvatars, setUserAvatars] = useState({});
    
    // References to store unsubscribe functions for snapshots
    const unsubscribersRef = useRef([]);

    // Add this function to fetch user avatar
    const fetchUserAvatar = async (userId) => {
        try {
            const response = await axios.get(`https://api.frazza.online/user/${userId}/avatar`);
            if (response.data.success && response.data.avatar) {
                return response.data.avatar;
            }
            return null;
        } catch (error) {
            console.error("Error fetching user avatar:", error);
            return null;
        }
    };

    // Add this useEffect to fetch avatars for all users
    useEffect(() => {
        const fetchAvatars = async () => {
            const avatarMap = {};

            // Fetch current user's avatar
            if (auth.currentUser) {
                const currentUserAvatar = await fetchUserAvatar(auth.currentUser.uid);
                if (currentUserAvatar) {
                    avatarMap[auth.currentUser.uid] = currentUserAvatar;
                }
            }

            // Fetch partner's avatar
            if (connectedUserId) {
                const partnerAvatar = await fetchUserAvatar(connectedUserId);
                if (partnerAvatar) {
                    avatarMap[connectedUserId] = partnerAvatar;
                }
            }

            setUserAvatars(avatarMap);
        };

        fetchAvatars();
    }, [connectedUserId]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((currentUser) => {
            if (currentUser) {
                setupRealTimeListeners(currentUser.uid);
            } else {
                // Clean up listeners
                unsubscribersRef.current.forEach(unsub => unsub());
                unsubscribersRef.current = [];
                
                setTodoList([]);
                setPartnerTodos([]);
                setLoading(false);
            }
        });

        return () => {
            unsubscribe();
            // Clean up all active listeners
            unsubscribersRef.current.forEach(unsub => unsub());
        };
    }, []);

    useEffect(() => {
        if (quickFilter === 'all') {
            resetFilters();
        } else if (quickFilter === 'today') {
            applyTodayFilter();
        } else if (quickFilter === 'week') {
            applyWeekFilter();
        } else if (quickFilter === 'pending') {
            setFilterStatus(false);
        } else if (quickFilter === 'completed') {
            setFilterStatus(true);
        }
    }, [quickFilter]);

    const applyFilters = () => {
        if (quickFilter === 'today') {
            applyTodayFilter();
        } else if (quickFilter === 'week') {
            applyWeekFilter();
        }
    };

    const setupRealTimeListeners = async (userId) => {
        setLoading(true);
        
        try {
            // Clean up any existing listeners
            unsubscribersRef.current.forEach(unsub => unsub());
            unsubscribersRef.current = [];

            // Get user info to find partner
            const userInfoDoc = await getDoc(doc(db, "UserInformation", userId));
            let partnerId = null;
            let userNameFromDoc = 'You';
            let partnerNameFromDoc = 'Partner';

            if (userInfoDoc.exists()) {
                const userData = userInfoDoc.data();
                userNameFromDoc = userData.name || 'You';
                partnerId = userData.inRelationWith;
                setUserName(userNameFromDoc);
                setConnectedUserId(partnerId);
            }

            // Get partner name if exists
            if (partnerId) {
                const partnerInfoDoc = await getDoc(doc(db, "UserInformation", partnerId));
                if (partnerInfoDoc.exists()) {
                    partnerNameFromDoc = partnerInfoDoc.data().name || 'Partner';
                    setPartnerName(partnerNameFromDoc);
                }
            }

            // Set up real-time listener for user's todos
            const userTodosRef = collection(db, "events", userId, "userTodos");
            const userUnsubscribe = onSnapshot(userTodosRef, (snapshot) => {
                const userTodos = [];
                snapshot.forEach(doc => {
                    const data = doc.data();
                    const dueDate = data.date ? new Date(data.date) : null;
                    const now = new Date();
                    const isActive = !dueDate || dueDate >= now;

                    userTodos.push({
                        ...data,
                        id: doc.id,
                        dueDate: dueDate,
                        owner: userNameFromDoc,
                        createdBy: data.createdBy || userNameFromDoc,
                        isCurrentUser: true,
                        active: data.active !== undefined ? data.active : isActive
                    });
                });
                
                setTodoList(userTodos);
                setLoading(false);
            }, (error) => {
                console.error("Error listening to user todos:", error);
                toast.current?.show({ 
                    severity: 'error', 
                    summary: 'Error', 
                    detail: 'Failed to load your todos' 
                });
                setLoading(false);
            });
            
            unsubscribersRef.current.push(userUnsubscribe);

            // Set up real-time listener for partner's todos if partner exists
            if (partnerId) {
                const partnerTodosRef = collection(db, "events", partnerId, "userTodos");
                const partnerUnsubscribe = onSnapshot(partnerTodosRef, (snapshot) => {
                    const partnerTodos = [];
                    snapshot.forEach(doc => {
                        const data = doc.data();
                        const dueDate = data.date ? new Date(data.date) : null;
                        const now = new Date();
                        const isActive = !dueDate || dueDate >= now;

                        partnerTodos.push({
                            ...data,
                            id: doc.id,
                            dueDate: dueDate,
                            owner: partnerNameFromDoc,
                            createdBy: data.createdBy || partnerNameFromDoc,
                            isCurrentUser: false,
                            active: data.active !== undefined ? data.active : isActive
                        });
                    });
                    
                    setPartnerTodos(partnerTodos);
                }, (error) => {
                    console.error("Error listening to partner todos:", error);
                    toast.current?.show({ 
                        severity: 'error', 
                        summary: 'Error', 
                        detail: 'Failed to load partner todos' 
                    });
                });
                
                unsubscribersRef.current.push(partnerUnsubscribe);
            } else {
                setPartnerTodos([]);
            }

        } catch (error) {
            console.error("Error setting up real-time listeners:", error);
            toast.current?.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: 'Failed to load todos' 
            });
            setLoading(false);
        }
    };

    const allTodos = [...todoList, ...partnerTodos];

    const resetFilters = () => {
        setFilterStatus(null);
        setFilterPriority(null);
        setFilterCategories([]);
        setSortField('dueDate');
        setSortOrder('asc');
        setSearchTerm('');
    };

    const applyTodayFilter = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Apply to all todos, not just todoList
        const processToday = (todo) => {
            if (todo.dueDate) {
                const dueDate = new Date(todo.dueDate);
                dueDate.setHours(0, 0, 0, 0);
                return { ...todo, isToday: dueDate.getTime() === today.getTime() };
            }
            return { ...todo, isToday: false };
        };

        setTodoList(prevList => prevList.map(processToday));
        setPartnerTodos(prevList => prevList.map(processToday));

        setFilterStatus(null);
    };

    const applyWeekFilter = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const endOfWeek = new Date(today);
        endOfWeek.setDate(today.getDate() + 7);

        // Apply to all todos, not just todoList
        const processWeek = (todo) => {
            if (todo.dueDate) {
                const dueDate = new Date(todo.dueDate);
                return { ...todo, isThisWeek: dueDate >= today && dueDate <= endOfWeek };
            }
            return { ...todo, isThisWeek: false };
        };

        setTodoList(prevList => prevList.map(processWeek));
        setPartnerTodos(prevList => prevList.map(processWeek));

        setFilterStatus(null);
    };

    const onSubmit = async () => {
        if (!auth.currentUser) {
            if (toast.current) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'User not authenticated' });
            } else {
                console.error('User not authenticated');
            }
            return;
        }

        if (!todo) {
            if (toast.current) {
                toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'Please enter a task' });
            } else {
                console.error('Please enter a task');
            }
            return;
        }

        if (!assigneeId) {
            if (toast.current) {
                toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'Please assign the task to someone' });
            } else {
                console.error('Please assign the task to someone');
            }
            return;
        }

        if (dueDate) {
            const now = new Date();
            now.setHours(0, 0, 0, 0); // Set to start of day for proper comparison

            const dueDateCopy = new Date(dueDate);
            dueDateCopy.setHours(0, 0, 0, 0); // Set to start of day for proper comparison

            if (dueDateCopy < now) {
                if (toast.current) {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'Due date cannot be in the past' });
                } else {
                    console.error('Due date cannot be in the past');
                }
                return;
            }
        }

        try {
            const newTodo = {
                todo: todo.trim(),
                dueDate: dueDate,
                status: false,
                priority: priority,
                category: category,
                createdBy: userName,
                assigneeId: assigneeId,
                assigneeName: assigneeName
            };

            await axios.post('https://api.frazza.online/todos', {
                userId: auth.currentUser.uid,
                todo: newTodo
            });

            if (toast.current) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: 'Task added successfully' });
            }

            // Reset form
            setTodo("");
            setDueDate(null);
            setPriority('medium');
            setCategory('');
            setAssigneeId(null);
            setAssigneeName(null);
            setDialogVisible(false);

            // No need to refresh - Firestore snapshot will handle the update automatically

        } catch (error) {
            if (toast.current) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to add task' });
            }
            console.error("Error adding todo:", error);
        }
    };

    const toggleStatus = async (todo) => {
        try {
            const userId = todo.isCurrentUser ? auth.currentUser.uid : connectedUserId;

            await axios.put(`https://api.frazza.online/todos/${todo.id}/status`, {
                userId: userId,
                status: !todo.status
            });

            if (toast.current) {
                toast.current.show({
                    severity: 'info',
                    summary: 'Status Updated',
                    detail: !todo.status ? 'Task completed' : 'Task marked as pending'
                });
            }

            // No need to refresh - Firestore snapshot will handle the update automatically
        } catch (error) {
            if (toast.current) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to update status' });
            }
            console.error("Error updating status:", error);
        }
    };

    const deleteTodo = (id, isCurrentUser) => {
        confirmDialog({
            message: 'Are you sure you want to delete this task?',
            header: 'Delete Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger todoList-dialog-delete-button',
            rejectClassName: 'p-button-text todoList-dialog-cancel-button',
            className: 'todoList-confirmDialog todoList-deleteTask-dialog',
            accept: async () => {
                try {
                    // Determine the correct user ID based on whether it's a partner todo
                    const userId = isCurrentUser ? auth.currentUser.uid : connectedUserId;

                    await axios.delete(`https://api.frazza.online/todos/${id}`, {
                        params: { userId }
                    });

                    if (toast.current) {
                        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Task deleted' });
                    }

                    // No need to refresh - Firestore snapshot will handle the update automatically
                } catch (error) {
                    if (toast.current) {
                        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete task' });
                    }
                    console.error("Error deleting todo:", error);
                }
            }
        });
    };

    const openEditDialog = (todo) => {
        setCurrentTodo({
            ...todo,
            dueDate: todo.date ? new Date(todo.date) : null
        });
        setEditDialogVisible(true);
    };

    const saveEditedTodo = async () => {
        if (!currentTodo.todo) {
            if (toast.current) {
                toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'Please enter a task' });
            } else {
                console.error('Please enter a task');
            }
            return;
        }

        if (!currentTodo.assigneeId) {
            if (toast.current) {
                toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'Please assign the task to someone' });
            } else {
                console.error('Please assign the task to someone');
            }
            return;
        }

        if (currentTodo.dueDate) {
            const now = new Date();
            now.setHours(0, 0, 0, 0); // Set to start of day for proper comparison

            const dueDateCopy = new Date(currentTodo.dueDate);
            dueDateCopy.setHours(0, 0, 0, 0); // Set to start of day for proper comparison

            if (dueDateCopy < now) {
                if (toast.current) {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'Due date cannot be in the past' });
                } else {
                    console.error('Due date cannot be in the past');
                }
                return;
            }
        }
        try {
            const todoData = {
                id: currentTodo.id,
                todo: currentTodo.todo,
                dueDate: currentTodo.dueDate,
                status: currentTodo.status,
                priority: currentTodo.priority,
                category: currentTodo.category,
                assigneeId: currentTodo.assigneeId,
                assigneeName: currentTodo.assigneeName
            };

            await axios.post('https://api.frazza.online/todos', {
                userId: auth.currentUser.uid,
                todo: todoData
            });

            if (toast.current) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: 'Task updated' });
            }
            setEditDialogVisible(false);

            // No need to refresh - Firestore snapshot will handle the update automatically
        } catch (error) {
            if (toast.current) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'You can not edit or modify you partners todos' });
            }
            console.error("Error updating todo:", error);
        }
    };

    const handleBatchComplete = async () => {
        confirmDialog({
            message: 'Mark all filtered tasks as complete?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-success todoList-dialog-submit-button',
            rejectClassName: 'p-button-text todoList-dialog-cancel-button',
            className: 'todoList-confirmDialog todoList-batchComplete-dialog',
            accept: async () => {
                try {
                    // Get IDs of incomplete todos that match the current filter
                    const todoIds = filteredTodos
                        .filter(todo => !todo.status && todo.isCurrentUser)
                        .map(todo => todo.id);

                    if (todoIds.length === 0) {
                        if (toast.current) {
                            toast.current.show({ 
                                severity: 'info', 
                                summary: 'Info', 
                                detail: 'No incomplete tasks to update' 
                            });
                        } else {
                            console.info('No incomplete tasks to update');
                        }
                        return;
                    }

                    // Call the batch complete endpoint
                    await axios.post('https://api.frazza.online/todos/batch/complete', {
                        userId: auth.currentUser.uid,
                        todoIds: todoIds
                    });

                    if (toast.current) {
                        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Tasks completed' });
                    }

                    // No need to refresh - Firestore snapshot will handle the update automatically
                } catch (error) {
                    if (toast.current) {
                        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to update tasks' });
                    }
                    console.error("Error updating tasks:", error);
                }
            }
        });
    };

    const handleBatchDelete = async () => {
        confirmDialog({
            message: 'Delete all completed tasks?',
            header: 'Delete Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger todoList-dialog-delete-button',
            rejectClassName: 'p-button-text todoList-dialog-cancel-button',
            className: 'todoList-confirmDialog todoList-batchDelete-dialog',
            accept: async () => {
                try {
                    // Call the batch delete endpoint
                    await axios.delete('https://api.frazza.online/todos/batch/completed', {
                        params: { userId: auth.currentUser.uid }
                    });

                    if (toast.current) {
                        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Completed tasks deleted' });
                    }

                    // No need to refresh - Firestore snapshot will handle the update automatically
                } catch (error) {
                    if (toast.current) {
                        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete tasks' });
                    }
                    console.error("Error deleting tasks:", error);
                }
            }
        });
    };

    const handleDeleteExpired = async () => {
        confirmDialog({
            message: 'Delete all expired tasks? This will remove all tasks with a due date in the past.',
            header: 'Delete Expired Tasks Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger todoList-dialog-delete-button',
            rejectClassName: 'p-button-text todoList-dialog-cancel-button',
            className: 'todoList-confirmDialog todoList-deleteExpired-dialog',
            accept: async () => {
                try {
                    // Call the batch delete expired endpoint
                    await axios.delete('https://api.frazza.online/todos/batch/expired', {
                        params: { userId: auth.currentUser.uid }
                    });

                    if (toast.current) {
                        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Expired tasks deleted' });
                    }

                    // No need to refresh - Firestore snapshot will handle the update automatically
                } catch (error) {
                    if (toast.current) {
                        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete expired tasks' });
                    }
                    console.error("Error deleting expired tasks:", error);
                }
            }
        });
    };

    // Apply filters and sorting
    const filteredTodos = allTodos.filter(todo => {
        // Search term filtering
        if (searchTerm && !todo.todo.toLowerCase().includes(searchTerm.toLowerCase())) {
            return false;
        }

        if (ownerFilter === true && !todo.isCurrentUser) {
            return false;
        }
        if (ownerFilter === false && todo.isCurrentUser) {
            return false;
        }
        // Quick filter for today
        if (quickFilter === 'today' && !todo.isToday) {
            return false;
        }

        // Quick filter for week
        if (quickFilter === 'week' && !todo.isThisWeek) {
            return false;
        }

        // Status filtering
        if (filterStatus !== null && todo.status !== filterStatus) {
            return false;
        }

        // Priority filtering
        if (filterPriority !== null && todo.priority !== filterPriority) {
            return false;
        }

        // Category filtering
        if (filterCategories && filterCategories.length > 0) {
            if (!todo.category || !filterCategories.includes(todo.category)) {
                return false;
            }
        }

        return true;
    }).sort((a, b) => {
        if (sortField === 'dueDate') {
            // Handle null dates properly
            if (!a.date && !b.date) return 0;
            if (!a.date) return sortOrder === 'asc' ? 1 : -1;
            if (!b.date) return sortOrder === 'asc' ? -1 : 1;
            return sortOrder === 'asc' ? a.date - b.date : b.date - a.date;
        }

        if (sortField === 'priority') {
            const priorityValue = { high: 3, medium: 2, low: 1 };
            const aValue = priorityValue[a.priority] || 0;
            const bValue = priorityValue[b.priority] || 0;
            return sortOrder === 'asc' ? bValue - aValue : aValue - bValue;
        }

        // For other fields like createdAt
        const aValue = a[sortField] || 0;
        const bValue = b[sortField] || 0;
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

    const handleSortChange = (e) => {
        setSortField(e.value.field);
        setSortOrder(e.value.order);
    };

    const itemTemplate = (todo) => {
        const priorityClass = `priority-${todo.priority || 'medium'}`;
        const statusClass = todo.status ? 'completed' : 'pending';
        const isOwner = todo.isCurrentUser;

        const isOverdue = todo.dueDate && !todo.status && (
            new Date(todo.dueDate).setHours(0,0,0,0) < new Date().setHours(0,0,0,0)
        );

        const displayName = todo.assigneeName || todo.createdBy || todo.owner;
        const isAssigned = !!todo.assigneeName;

        // Get the user ID for avatar lookup - use creator's ID if not assigned
        const userId = todo.assigneeId || (todo.isCurrentUser ? auth.currentUser?.uid : connectedUserId);
        const creatorId = todo.isCurrentUser ? auth.currentUser?.uid : connectedUserId;
        const avatarId = userAvatars[isAssigned ? userId : creatorId];
        const avatarSrc = avatarId ? avatars.find(a => a.id === avatarId)?.src : null;

        const handleItemClick = (e) => {
            // Don't open edit dialog if clicking on checkbox or delete button
            if (e.target.closest('.todo-checkbox') || e.target.closest('.p-button')) {
                return;
            }
            openEditDialog(todo);
        };

        return (
            <div 
                className={`todo-item ${statusClass} ${priorityClass} ${!isOwner ? 'partner-todo' : ''} ${isOverdue ? 'overdue-todo' : ''}`}
                onClick={handleItemClick}
            >
                <div className="todo-item-content">
                    <div className="todo-avatar">
                        {avatarSrc ? (
                            <img src={avatarSrc} alt={displayName} className="avatar-image" />
                        ) : (
                            <div className="avatar-circle">
                                {displayName ? displayName.charAt(0).toUpperCase() : '?'}
                            </div>
                        )}
                    </div>
                    <div className="todo-details">
                        <h4 className={`${todo.status ? 'line-through' : ''} ${isOverdue ? 'overdue' : ''} `}>
                            {todo.todo}
                        </h4>
                        <div className="todo-meta">
                            {todo.dueDate && (
                                <span className="due-date">
                                <i className="pi pi-calendar"></i> {todo.dueDate instanceof Date ? todo.dueDate.toLocaleDateString() : (typeof todo.dueDate === 'string' ? new Date(todo.dueDate).toLocaleDateString() : 'No date')}
                            </span>
                            )}
                            {todo.category && (
                                <span className="category">
                                <i className="pi pi-tag"></i> {todo.category}
                            </span>
                            )}
                            <span className={`priority ${priorityClass}`}>
                                <i className="pi pi-flag"></i> {todo.priority || 'medium'}
                            </span>
                            {isAssigned && (
                                <span className="assignee">
                                    <i className="pi pi-user-edit"></i> Assigned to: {todo.assigneeName}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="todo-actions">
                        {!isOverdue && (
                            <div className="todo-checkbox" onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                    checked={todo.status}
                                    onChange={() => toggleStatus(todo)}
                                />
                            </div>
                        )}
                        <Button 
                            icon="pi pi-trash" 
                            className="p-button-rounded p-button-text p-button-danger"
                            onClick={(e) => {
                                e.stopPropagation();
                                deleteTodo(todo.id, todo.isCurrentUser);
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="todo-list-container">
            <Toast ref={toast} />
            <ConfirmDialog className="todoList-confirmDialog" />
            <h1>My Tasks</h1>
            <div className="todo-header">
                <div className="todo-header-actions">
                    <Button
                        label="Add Task"
                        onClick={() => setDialogVisible(true)}
                    />
                    <Button
                        icon="pi pi-filter"
                        className="p-button-rounded p-button-outlined"
                        onClick={() => setFilterSidebarVisible(true)}
                        tooltip="Filters"
                    />
                </div>
            </div>
            <div className="search-box-todo">
                <i className="pi pi-search search-icon"/>
                <InputText
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search tasks..."
                    className="main-search"
                />
            </div>
            {!isMobile && (
                <div className="quick-filters-below-search">
                    <SelectButton
                        value={quickFilter}
                        options={quickFilterOptions}
                        onChange={(e) => setQuickFilter(e.value)}
                        className="quick-filter-buttons"
                    />
                </div>
            )}

            <div className="active-filters">
                {filterStatus !== null && (
                    <Chip
                        label={filterStatus ? "Completed" : "Pending"}
                        removable
                        onRemove={() => setFilterStatus(null)}
                    />
                )}
                {filterPriority && (
                    <Chip
                        label={`Priority: ${filterPriority}`}
                        removable
                        onRemove={() => setFilterPriority(null)}
                    />
                )}
                {filterCategories && filterCategories.map(cat => (
                    <Chip
                        key={cat}
                        label={cat}
                        removable
                        onRemove={() => setFilterCategories(filterCategories.filter(c => c !== cat))}
                    />
                ))}

            </div>

            {loading ? (
                <div className="loading">
                    <Hearts height="80" width="80" color="#f97316" ariaLabel="hearts-loading" visible={true} />
                </div>
            ) : filteredTodos.length > 0 ? (
                <DataView
                    value={filteredTodos}
                    itemTemplate={itemTemplate}
                    className="task-list"
                />
            ) : (
                <div className="empty-state">
                    <i className="pi pi-check-circle"></i>
                    <h3>No tasks found</h3>
                    <p className="noTaskFoundTxt">Add a new task or change your filters</p>
                </div>
            )}

            <div className="batch-action-container">
                <div className="batch-action-card">
                    <div className="batch-actions">
                        <div className="batch-action-item-right">
                        <Button
                            label="Complete All"
                            className="p-button-success"
                            onClick={handleBatchComplete}
                        />
                        </div>
                        <div className="batch-action-item-right">
                        <Button
                            label="Delete expired"
                            className="p-button-danger"
                            onClick={handleDeleteExpired}
                        />
                            <Button
                                label="Delete Completed"
                                className="p-button-danger"
                                onClick={handleBatchDelete}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Sidebar
                visible={filterSidebarVisible}
                position="right"
                onHide={() => setFilterSidebarVisible(false)}
                className="filter-sidebar"
                header="Filter & Sort Tasks"
            >
                {isMobile && (
                    <div className="quick-filters">
                        <h3>Quick Filters</h3>
                        <SelectButton
                            value={quickFilter}
                            options={quickFilterOptions}
                            onChange={(e) => setQuickFilter(e.value)}
                            className="quick-filter-buttons"
                        />
                    </div>
                )}
                <div className="filter-sidebar-content">
                    <div className="quick-filters">
                        <h3>Quick Filters</h3>
                        <SelectButton
                            value={quickFilter}
                            options={quickFilterOptions}
                            onChange={(e) => setQuickFilter(e.value)}
                            className="quick-filter-buttons"
                        />
                    </div>
                    <Accordion multiple>
                        <AccordionTab header="Status">
                            <div className="p-field-radiobutton">
                                <Button
                                    label="All"
                                    className={filterStatus === null ? "p-button-outlined p-button-info" : "p-button-text"}
                                    onClick={() => setFilterStatus(null)}
                                />
                                <Button
                                    label="Pending"
                                    className={filterStatus === false ? "p-button-outlined p-button-info" : "p-button-text"}
                                    onClick={() => setFilterStatus(false)}
                                />
                                <Button
                                    label="Completed"
                                    className={filterStatus === true ? "p-button-outlined p-button-info" : "p-button-text"}
                                    onClick={() => setFilterStatus(true)}
                                />
                            </div>
                        </AccordionTab>

                        <AccordionTab header="Priority">
                            <div className="p-field">
                                <Dropdown
                                    value={filterPriority}
                                    options={[
                                        { label: 'All', value: null },
                                        ...priorityOptions
                                    ]}
                                    onChange={(e) => setFilterPriority(e.value)}
                                    placeholder="Select Priority"
                                    className="w-full"
                                />
                            </div>
                        </AccordionTab>

                        <AccordionTab header="Category">
                            <div className="p-field">
                                <MultiSelect
                                    value={filterCategories}
                                    options={categoryOptions}
                                    onChange={(e) => setFilterCategories(e.value)}
                                    placeholder="Select Categories"
                                    display="chip"
                                    className="w-full"
                                    showClear
                                />
                            </div>
                        </AccordionTab>

                        <AccordionTab header="Sort By">
                            <div className="p-field">
                                <Dropdown
                                    value={sortOptions.find(option =>
                                        option.value.field === sortField && option.value.order === sortOrder)}
                                    options={sortOptions}
                                    onChange={handleSortChange}
                                    optionLabel="label"
                                    placeholder="Sort by"
                                    className="w-full"
                                />
                            </div>
                        </AccordionTab>
                    </Accordion>

                    <Divider />

                    <Button
                        label="Reset All Filters"
                        icon="pi pi-refresh"
                        onClick={resetFilters}
                        className="p-button-outlined w-full"
                    />
                </div>
            </Sidebar>

            <Dialog
                header="Add New Task"
                visible={dialogVisible}
                onHide={() => setDialogVisible(false)}
                className="todoList-dialog todoList-addTask-dialog frazza-dialog-md"
                footer={
                    <>
                        <Button label="Cancel" icon="pi pi-times" onClick={() => setDialogVisible(false)} className="p-button-text" />
                        <Button label="Add Task" icon="pi pi-check" onClick={onSubmit} autoFocus />
                    </>
                }
            >
                <div className="todoList-dialog-form todoList-addTask-form">
                    <div className="frazza-form-group">
                        <label htmlFor="todo" className="frazza-form-label required">Task Name</label>
                        <InputText
                            id="todo"
                            value={todo}
                            onChange={(e) => setTodo(e.target.value)}
                            className="frazza-input"
                            placeholder="Enter task name"
                        />
                    </div>

                    <div className="todoList-form-row">
                        <div className="frazza-form-group">
                            <label htmlFor="dueDate" className="frazza-form-label">Due Date</label>
                            <Calendar
                                id="dueDate"
                                value={dueDate}
                                onChange={(e) => setDueDate(e.value)}
                                showIcon
                                className="frazza-input"
                                minDate={new Date()}
                                placeholder="Select due date"
                            />
                        </div>

                        <div className="frazza-form-group">
                            <label htmlFor="priority" className="frazza-form-label">Priority</label>
                            <Dropdown
                                id="priority"
                                value={priority}
                                options={priorityOptions}
                                onChange={(e) => setPriority(e.value)}
                                className="frazza-select"
                                placeholder="Select priority"
                            />
                        </div>
                    </div>

                    <div className="frazza-form-group">
                        <label htmlFor="category" className="frazza-form-label">Category</label>
                        <Dropdown
                            id="category"
                            value={category}
                            options={categoryOptions}
                            onChange={(e) => setCategory(e.value)}
                            className="frazza-select"
                            placeholder="Select category"
                        />
                    </div>

                    <div className="frazza-form-group">
                        <label htmlFor="assignee" className="frazza-form-label">Assign To</label>
                        <Dropdown
                            id="assignee"
                            value={assigneeId}
                            options={getAssigneeOptions()}
                            onChange={(e) => {
                                setAssigneeId(e.value);
                                // Find the selected option to get the name
                                const selectedOption = getAssigneeOptions().find(option => option.value === e.value);
                                setAssigneeName(selectedOption ? selectedOption.name : null);
                            }}
                            className="frazza-select"
                            placeholder="Select assignee"
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog
                header="Edit Task"
                visible={editDialogVisible}
                onHide={() => setEditDialogVisible(false)}
                className="todoList-dialog todoList-editTask-dialog frazza-dialog-md"
                footer={
                    <>
                        <Button label="Cancel" icon="pi pi-times" onClick={() => setEditDialogVisible(false)} className="p-button-text" />
                        <Button label="Save" icon="pi pi-check" onClick={saveEditedTodo} autoFocus />
                    </>
                }
            >
                {currentTodo && (
                    <div className="todoList-dialog-form todoList-editTask-form">
                        <div className="frazza-form-group">
                            <label htmlFor="editTodo" className="frazza-form-label">Task</label>
                            <InputText
                                id="editTodo"
                                value={currentTodo.todo}
                                onChange={(e) => setCurrentTodo({...currentTodo, todo: e.target.value})}
                                className="frazza-input"
                            />
                        </div>

                        <div className="frazza-form-group">
                            <label htmlFor="editDueDate" className="frazza-form-label">Due Date</label>
                            <Calendar
                                id="editDueDate"
                                value={currentTodo.dueDate}
                                onChange={(e) => setCurrentTodo({...currentTodo, dueDate: e.value})}
                                showIcon
                                className="frazza-input"
                                minDate={new Date()}
                            />
                        </div>

                        <div className="frazza-form-group">
                            <label htmlFor="editPriority" className="frazza-form-label">Priority</label>
                            <Dropdown
                                id="editPriority"
                                value={currentTodo.priority}
                                options={priorityOptions}
                                onChange={(e) => setCurrentTodo({...currentTodo, priority: e.value})}
                                className="frazza-select"
                            />
                        </div>

                        <div className="frazza-form-group">
                            <label htmlFor="editCategory" className="frazza-form-label">Category</label>
                            <Dropdown
                                id="editCategory"
                                value={currentTodo.category}
                                options={categoryOptions}
                                onChange={(e) => setCurrentTodo({...currentTodo, category: e.value})}
                                className="frazza-select"
                            />
                        </div>

                        <div className="frazza-form-group">
                            <label htmlFor="editAssignee" className="frazza-form-label">Assign To</label>
                            <Dropdown
                                id="editAssignee"
                                value={currentTodo.assigneeId}
                                options={getAssigneeOptions()}
                                onChange={(e) => {
                                    const selectedOption = getAssigneeOptions().find(option => option.value === e.value);
                                    setCurrentTodo({
                                        ...currentTodo, 
                                        assigneeId: e.value,
                                        assigneeName: selectedOption ? selectedOption.name : null
                                    });
                                }}
                                className="frazza-select"
                                placeholder="Select Assignee"
                            />
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};
